import { useTranslate } from "ra-core";

import { cn } from "@/lib/utils";
import "@/styles/list-skeleton.css";

export type ListSkeletonVariant = "cards" | "table" | "grid" | "board" | "auto";

export interface ListSkeletonProps {
  /**
   * - `cards`: stacked glass cards, like the mobile lists.
   * - `table`: a table card with a header and rows (desktop DataTables).
   * - `grid`: tiles (companies, styles).
   * - `board`: Kanban columns.
   * - `auto`: `cards` on phones, `table` from md up (pure CSS, no flash).
   */
  variant?: ListSkeletonVariant;
  /** Number of placeholder rows / cards / tiles. */
  rows?: number;
  /** Table columns (table and auto variants). */
  columns?: number;
  /** Leading round avatar on cards. */
  avatar?: boolean;
  className?: string;
}

/** Deterministic, varied bar widths so the placeholders look like content. */
const WIDTHS = ["w-2/5", "w-3/5", "w-1/2", "w-2/3", "w-1/3", "w-[55%]"];
const width = (index: number, offset = 0) =>
  WIDTHS[(index + offset) % WIDTHS.length];

const range = (count: number) => Array.from({ length: count }, (_, i) => i);

const Bar = ({ className }: { className?: string }) => (
  <span aria-hidden className={cn("list-skeleton-bar block h-3", className)} />
);

const SkeletonCards = ({ rows, avatar }: { rows: number; avatar: boolean }) => (
  <div className="flex flex-col gap-3">
    {range(rows).map((index) => (
      <div
        key={index}
        data-testid="list-skeleton-item"
        className="record-card flex items-center gap-3 rounded-3xl px-4 py-3.5"
      >
        {avatar && <Bar className="size-10 shrink-0 rounded-full" />}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Bar className={cn("h-3.5", width(index))} />
          <Bar className={cn("h-2.5", width(index, 3))} />
        </div>
        <Bar className="h-5 w-16 shrink-0" />
      </div>
    ))}
  </div>
);

const SkeletonTable = ({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}) => (
  <div className="overflow-hidden rounded-xl border bg-card shadow-card">
    <div className="flex gap-6 border-b px-4 py-3">
      {range(columns).map((column) => (
        <Bar key={column} className="h-2.5 flex-1 opacity-70" />
      ))}
    </div>
    {range(rows).map((index) => (
      <div
        key={index}
        data-testid="list-skeleton-item"
        className="flex items-center gap-6 border-b px-4 py-3.5 last:border-b-0"
      >
        {range(columns).map((column) => (
          <div key={column} className="flex-1">
            <Bar className={width(index, column)} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const SkeletonGrid = ({ rows }: { rows: number }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
    {range(rows).map((index) => (
      <div
        key={index}
        data-testid="list-skeleton-item"
        className="record-card flex h-44 flex-col items-center justify-center gap-3 rounded-2xl p-4"
      >
        <Bar className="size-12 rounded-full" />
        <Bar className={cn("h-3", width(index))} />
        <Bar className={cn("h-2.5", width(index, 2))} />
      </div>
    ))}
  </div>
);

const SkeletonBoard = ({ rows }: { rows: number }) => (
  <div className="flex gap-4 overflow-hidden">
    {range(4).map((column) => (
      <div key={column} className="flex min-w-56 flex-1 flex-col gap-3">
        <Bar className={cn("h-3.5", width(column))} />
        {range(Math.max(1, rows - column)).map((index) => (
          <div
            key={index}
            data-testid="list-skeleton-item"
            className="record-card flex flex-col gap-2 rounded-2xl p-3"
          >
            <Bar className={cn("h-3", width(index, column))} />
            <Bar className="h-2.5 w-1/3" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Placeholder shown while a list loads, so "loading" never looks like "empty".
 * Fades in after a short delay (no flash on fast loads) with a subtle
 * shimmer; both are disabled under prefers-reduced-motion.
 */
export const ListSkeleton = ({
  variant = "auto",
  rows = 6,
  columns = 4,
  avatar = true,
  className,
}: ListSkeletonProps) => {
  const translate = useTranslate();
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="list-skeleton"
      className={cn("list-skeleton w-full", className)}
    >
      <span className="sr-only">
        {translate("crm.common.loading", { _: "Loading..." })}
      </span>
      {variant === "cards" && <SkeletonCards rows={rows} avatar={avatar} />}
      {variant === "table" && <SkeletonTable rows={rows} columns={columns} />}
      {variant === "grid" && <SkeletonGrid rows={rows} />}
      {variant === "board" && <SkeletonBoard rows={rows} />}
      {variant === "auto" && (
        <>
          <div className="md:hidden">
            <SkeletonCards rows={rows} avatar={avatar} />
          </div>
          <div className="hidden md:block">
            <SkeletonTable rows={rows} columns={columns} />
          </div>
        </>
      )}
    </div>
  );
};
